/**
 * Seule API de donnees que l'UI a le droit d'appeler.
 *
 * Elle garantit les deux invariants du domaine :
 *
 *  1. les evenements sont dedoublonnes par construction (`id = type-instant`),
 *     ce qui rend toute reingestion inoffensive ;
 *  2. **une nuit corrigee n'est jamais recalculee** — sans quoi la synchro du
 *     lendemain matin effacerait l'arbitrage de la veille.
 */

import { deriverNuits } from '../analysis/nuits'
import { MINUTE_MS, nowInstant, toInstant } from '../domain/time'
import {
  REGLAGES_DEFAUT,
  type Anomalie,
  type DayKey,
  type Evenement,
  type Instant,
  type Interruption,
  type Nuit,
  type Reglages,
  type TypeEvenement,
} from '../domain/types'
import { META_DERNIERE_SYNCHRO, META_REGLAGES, db, ecrireMeta, lireMeta } from './db'

// ---------------------------------------------------------------------------
// Reglages
// ---------------------------------------------------------------------------

export async function lireReglages(): Promise<Reglages> {
  const stocke = await lireMeta<Partial<Reglages>>(META_REGLAGES, {})
  // Fusion avec les defauts : un reglage ajoute dans une version ulterieure ne
  // doit pas rendre `undefined` un seuil dont dependent les calculs.
  return { ...REGLAGES_DEFAUT, ...stocke }
}

export async function ecrireReglages(patch: Partial<Reglages>): Promise<Reglages> {
  const fusion = { ...(await lireReglages()), ...patch }
  await ecrireMeta(META_REGLAGES, fusion)
  // Un seuil qui change change le decoupage : on rejoue l'historique.
  await recalculerNuits()
  return fusion
}

// ---------------------------------------------------------------------------
// Ingestion
// ---------------------------------------------------------------------------

export interface ResultatIngestion {
  recus: number
  nouveaux: number
  ignores: string[]
}

/**
 * Enregistre des evenements puis rejoue la derivation.
 *
 * `bulkPut` et non `bulkAdd` : reingerer un contenu deja lu doit etre un
 * non-evenement, pas une erreur. C'est ce qui autorise le raccourci du matin a
 * renvoyer tout son fichier sans se soucier de ce qui a deja ete transmis.
 */
export async function ingererEvenements(
  evenements: Evenement[],
  ignores: string[] = [],
): Promise<ResultatIngestion> {
  if (evenements.length === 0) {
    return { recus: 0, nouveaux: 0, ignores }
  }

  const ids = evenements.map((e) => e.id)
  const existants = await db.evenements.bulkGet(ids)
  const nouveaux = existants.filter((e) => e === undefined).length

  await db.evenements.bulkPut(evenements)
  await ecrireMeta(META_DERNIERE_SYNCHRO, nowInstant())
  await recalculerNuits()

  return { recus: evenements.length, nouveaux, ignores }
}

/**
 * Battement de l'app elle-meme, seule source du repli heuristique.
 *
 * Arrondi a 5 minutes : sans cet arrondi, une soiree passee l'app ouverte
 * produirait un evenement par appel et noierait la base. Avec lui, l'identifiant
 * est stable a l'interieur du quart d'heure et `put` devient idempotent.
 */
export async function enregistrerBattement(maintenant = Date.now()): Promise<void> {
  const pas = 5 * MINUTE_MS
  const at = toInstant(Math.floor(maintenant / pas) * pas)
  const type: TypeEvenement = 'appli'
  await db.evenements.put({ id: `${type}-${at}`, type, at, creeLe: nowInstant() })
}

// ---------------------------------------------------------------------------
// Derivation
// ---------------------------------------------------------------------------

/**
 * Rejoue le decoupage sur l'ensemble des evenements.
 *
 * Les nuits corrigees sont laissees intactes, et les nuits automatiques qui ne
 * sont plus produites sont retirees — sinon un reglage plus strict laisserait
 * derriere lui des nuits fantomes qu'aucun evenement ne justifie plus.
 */
export async function recalculerNuits(): Promise<void> {
  const [evenements, reglages, existantes] = await Promise.all([
    db.evenements.toArray(),
    lireReglages(),
    db.nuits.toArray(),
  ])

  const { nuits } = deriverNuits(evenements, reglages)
  const figees = new Set(existantes.filter((n) => n.corrigee).map((n) => n.date))
  const aEcrire = nuits.filter((n) => !figees.has(n.date))
  const gardees = new Set([...figees, ...aEcrire.map((n) => n.date)])
  const aSupprimer = existantes.filter((n) => !gardees.has(n.date)).map((n) => n.date)

  await db.transaction('rw', db.nuits, async () => {
    if (aSupprimer.length > 0) await db.nuits.bulkDelete(aSupprimer)
    if (aEcrire.length > 0) await db.nuits.bulkPut(aEcrire)
  })
}

/** Les anomalies ne sont pas stockees : elles se deduisent des evenements et
 *  disparaissent d'elles-memes des que la nuit est corrigee. */
export async function listerAnomalies(): Promise<Anomalie[]> {
  const [evenements, reglages, nuits] = await Promise.all([
    db.evenements.toArray(),
    lireReglages(),
    db.nuits.toArray(),
  ])
  const dejaResolues = new Set(nuits.map((n) => n.date))
  return deriverNuits(evenements, reglages).anomalies.filter((a) => !dejaResolues.has(a.date))
}

// ---------------------------------------------------------------------------
// Lecture
// ---------------------------------------------------------------------------

export async function listerNuits(limite = 400): Promise<Nuit[]> {
  const tout = await db.nuits.toArray()
  return tout.sort((a, b) => b.date.localeCompare(a.date)).slice(0, limite)
}

export async function lireNuit(date: DayKey): Promise<Nuit | undefined> {
  return db.nuits.get(date)
}

export async function derniereNuit(): Promise<Nuit | undefined> {
  const nuits = await listerNuits(1)
  return nuits[0]
}

export async function listerEvenements(limite = 500): Promise<Evenement[]> {
  const tout = await db.evenements.toArray()
  return tout.sort((a, b) => b.at.localeCompare(a.at)).slice(0, limite)
}

// ---------------------------------------------------------------------------
// Arbitrage
//
// Toute intervention passe la nuit en `corrigee`. C'est ce qui la met a l'abri
// du prochain recalcul : sans cela, le geste du matin serait defait par la
// synchro du lendemain.
// ---------------------------------------------------------------------------

async function figer(date: DayKey, patch: Partial<Nuit>): Promise<void> {
  await db.nuits.update(date, { ...patch, corrigee: true, modifieLe: nowInstant() })
}

/** Bascule une interruption entre « reveil decompte » et « ignoree ». */
export async function basculerInterruption(date: DayKey, index: number): Promise<void> {
  const nuit = await db.nuits.get(date)
  if (!nuit) return
  const interruptions = nuit.interruptions.map((i, k) =>
    k === index ? { ...i, ignoree: !i.ignoree } : i,
  )
  await figer(date, { interruptions })
}

/** Retient un autre candidat comme heure de coucher. */
export async function choisirDebut(date: DayKey, at: Instant, type: TypeEvenement): Promise<void> {
  const nuit = await db.nuits.get(date)
  if (!nuit) return
  // Les interruptions anterieures au nouveau debut n'en sont plus : elles
  // tombent hors de la nuit.
  const interruptions = nuit.interruptions.filter((i) => i.debut > at)
  await figer(date, { debut: at, origineDebut: type, interruptions })
}

/** Retient un autre candidat comme heure de lever. */
export async function choisirFin(date: DayKey, at: Instant, type: TypeEvenement): Promise<void> {
  const nuit = await db.nuits.get(date)
  if (!nuit) return
  const interruptions = nuit.interruptions.filter((i) => i.fin < at)
  await figer(date, { fin: at, origineFin: type, interruptions })
}

export async function majNuit(date: DayKey, patch: Partial<Nuit>): Promise<void> {
  await figer(date, patch)
}

export async function ajouterInterruption(
  date: DayKey,
  interruption: Interruption,
): Promise<void> {
  const nuit = await db.nuits.get(date)
  if (!nuit) return
  const interruptions = [...nuit.interruptions, interruption].sort((a, b) =>
    a.debut.localeCompare(b.debut),
  )
  await figer(date, { interruptions })
}

export async function supprimerInterruption(date: DayKey, index: number): Promise<void> {
  const nuit = await db.nuits.get(date)
  if (!nuit) return
  await figer(date, { interruptions: nuit.interruptions.filter((_, k) => k !== index) })
}

/** Saisie entierement manuelle, pour rattraper une nuit qu'aucun signal n'a couverte. */
export async function creerNuitManuelle(
  date: DayKey,
  debut: Instant,
  fin: Instant,
  notes?: string,
): Promise<void> {
  await db.nuits.put({
    date,
    debut,
    fin,
    interruptions: [],
    candidatsDebut: [],
    candidatsFin: [],
    origineDebut: 'appli',
    origineFin: 'appli',
    source: 'manuel',
    corrigee: true,
    ...(notes ? { notes } : {}),
    modifieLe: nowInstant(),
  })
}

/**
 * Supprime la nuit et rejoue la derivation.
 *
 * Le recalcul est necessaire : sans lui, supprimer une nuit corrigee la ferait
 * disparaitre alors que les evenements qui la justifient sont toujours la.
 * Apres recalcul, elle revient dans sa forme automatique — ce qui est bien le
 * sens attendu de « annuler ma correction ».
 */
export async function supprimerNuit(date: DayKey): Promise<void> {
  await db.nuits.delete(date)
  await recalculerNuits()
}
